import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { MfaReminderToast } from '@/components/auth/MfaReminderToast';
import { useUserWithRoles } from '@/hooks/queries/useUser';
import { authService, isTokenExpired } from '@/services/apiClient';
import { RootState } from '@/store';
import {
  loginSuccess,
  loginFailure,
  logout as logoutAction,
  clearError,
  setUserData,
  tokenExpired,
} from '@/store/slices/authSlice';
import { AuthResponse, LoginForm } from '@/types/auth';
import { UserRole } from '@/types/user';

import { supabase } from '../services/supabase';

export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, token, expiresAt, userContext, isAuthenticated, isLoading, error } = useSelector(
    (state: RootState) => state.auth
  );

  // Get stored user ID for fetching user data
  const storedUserId = localStorage.getItem('finara_user_id');

  // Fetch user data if we have a token but no user data
  const shouldFetchUserData = Boolean(isAuthenticated && token && !user && storedUserId);
  const {
    user: fetchedUser,
    userContext: fetchedUserContext,
    isLoading: userDataLoading,
    error: userDataError,
  } = useUserWithRoles(storedUserId || '', token || '', shouldFetchUserData);

  // Check token expiration on mount and periodically
  useEffect(() => {
    if (expiresAt && isTokenExpired(expiresAt)) {
      dispatch(tokenExpired());
      void navigate('/login', { replace: true });
    }

    const interval = setInterval(() => {
      if (expiresAt && isTokenExpired(expiresAt)) {
        dispatch(tokenExpired());
        void navigate('/login', { replace: true });
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [expiresAt, dispatch, navigate]);

  // Update Redux state when user data is fetched
  useEffect(() => {
    if (fetchedUser && fetchedUserContext && shouldFetchUserData) {
      dispatch(
        setUserData({
          user: fetchedUser,
          userContext: fetchedUserContext,
        })
      );
    }
  }, [fetchedUser, fetchedUserContext, shouldFetchUserData, dispatch]);

  // Handle user data fetch error
  useEffect(() => {
    if (userDataError) {
      console.error('Error al obtener datos del usuario:', userDataError);
      if (
        userDataError instanceof Error &&
        'status' in userDataError &&
        userDataError.status === 401
      ) {
        dispatch(tokenExpired());
        void navigate('/login', { replace: true });
      }
    }
  }, [userDataError, dispatch, navigate]);

  const login = async (
    credentials: LoginForm
  ): Promise<{ mfaRequired: boolean; factorId?: string; authData?: AuthResponse }> => {
    try {
      const authResponse = await authService.login(credentials);

      // Check if user role is client or employee and block access
      // admin_onboarding users are allowed through — they go to the funnel
      if (authResponse.user.role === 'client' || authResponse.user.role === 'employee') {
        dispatch(
          loginFailure(
            'Acceso no autorizado. Solo administradores pueden acceder a esta plataforma.'
          )
        );
        return { mfaRequired: false };
      }

      // Check for MFA requirement
      let mfaFactor = null;
      let supabaseSession = null;
      try {
        const supabaseResult = await supabase.auth.signInWithPassword({
          email: credentials.email,
          password: credentials.password,
        });

        if (supabaseResult.error) {
          console.warn('Error de autenticación en Supabase:', supabaseResult.error.message);
        } else {
          supabaseSession = supabaseResult.data.session;

          // Check if MFA is required
          const { mfaService: mfaServiceImport } = await import('../services/mfa.service');
          mfaFactor = await mfaServiceImport.getVerifiedFactor();

          // If MFA is required, use Supabase token instead of backend token
          if (mfaFactor) {
            // Override auth response with Supabase token
            authResponse.token = supabaseSession.access_token;
            authResponse.expiresAt = new Date(supabaseSession.expires_at! * 1000).getTime();
          }
        }
      } catch (error) {
        console.warn('Error al verificar MFA:', error);
      }

      // If MFA is required, return auth data for modal
      if (mfaFactor) {
        return { mfaRequired: true, factorId: mfaFactor.id, authData: authResponse };
      }

      // No MFA - dispatch success and navigate
      dispatch(loginSuccess(authResponse));
      const redirectPath = getRedirectPath(authResponse.user.role as UserRole);
      void navigate(redirectPath, { replace: true });

      // Skip MFA reminder for admin onboarding users — they have no org yet
      if (authResponse.user.role === 'admin_onboarding') {
        return { mfaRequired: false };
      }

      // Show MFA setup reminder after navigation
      setTimeout(() => {
        toast(
          t => (
            <MfaReminderToast
              toastId={t.id}
              onConfigure={() => {
                void navigate('/configuracion');
              }}
            />
          ),
          {
            duration: 8000,
            position: 'bottom-left',
            style: {
              background: '#1f2937',
              color: '#fff',
              padding: '12px 16px',
              borderRadius: '8px',
              maxWidth: '420px',
            },
          }
        );
      }, 1500);

      return { mfaRequired: false };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al iniciar sesión';
      dispatch(loginFailure(errorMessage));
      return { mfaRequired: false };
    }
  };

  const logout = async () => {
    // Sign out from Supabase
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.warn('Error al cerrar sesión en Supabase:', error);
    }

    // Clear backend auth state
    dispatch(logoutAction());
    void navigate('/login', { replace: true });
  };

  const clearAuthError = () => {
    dispatch(clearError());
  };

  const getRedirectPath = (userRole: UserRole): string => {
    switch (userRole) {
      case 'super_admin':
        return '/dashboard';
      case 'admin':
      case 'manager':
        return '/dashboard-empresarial';
      case 'admin_onboarding':
        return '/admin-onboarding/funnel';
      case 'employee':
      case 'client':
        return '/login';
      default:
        return '/login';
    }
  };

  const canAccessRoute = (route: string, userRole: UserRole): boolean => {
    // Clients and employees cannot access any protected routes
    if (userRole === 'client' || userRole === 'employee') {
      return route === '/login';
    }

    // Super admin routes
    if (route.startsWith('/dashboard') || route.startsWith('/clientes')) {
      return userRole === 'super_admin';
    }

    // Admin/Manager routes
    if (route.startsWith('/admin/')) {
      return userRole === 'admin' || userRole === 'manager';
    }

    // Public routes
    if (route === '/login') {
      return true;
    }

    return false;
  };

  return {
    user,
    token,
    userContext,
    isAuthenticated,
    isLoading: isLoading || userDataLoading,
    error,
    login,
    logout,
    clearAuthError,
    dispatch,
    getRedirectPath,
    canAccessRoute,
  };
};
